import React, { useCallback, useRef, useState, useEffect } from 'react';
import { Button } from 'devextreme-react/button';
import Chat from 'devextreme-react/chat';
import type { ChatTypes } from 'devextreme-react/chat';
import { LoadPanel } from 'devextreme-react/load-panel';
import { loadMessages } from 'devextreme-react/common/core/localization';
import {
    user,
    assistant,
    CHAT_DISABLED_CLASS,
    CHAT_LOADING_CLASS,
} from './data';
import Message from './Message';
import { useApi } from './useApi';

import './assistant.scss';

export default function Assistant({ aiGroupId, enabled = true }: { aiGroupId?: number | null; enabled?: boolean } = {}) {

    const {
        load,
        reset,
        alerts,
        isLoading,
        insertMessage,
        fetchAIResponse,
        dataSource
    } = useApi();

    const containerRef = useRef<HTMLDivElement>(null);

    loadMessages({
        en: {
            'dxChat-emptyListMessage': 'Chat is Empty',
            'dxChat-emptyListPrompt': 'AI Assistant is ready to answer your questions.',
            'dxChat-textareaPlaceholder': 'Ask AI Assistant...',
        },
    });

    const [typingUsers, setTypingUsers] = useState<ChatTypes.User[]>([]);
    const [isProcessing, setIsProcessing] = useState<boolean>(false);

    useEffect(() => {
        if (enabled) {
            load(aiGroupId ?? null);
        }
    }, [load, aiGroupId, enabled]);

    const processAIRequest = useCallback(async (message: ChatTypes.Message): Promise<void> => {
        setIsProcessing(true);
        setTypingUsers([assistant]);

        await fetchAIResponse(message);

        setTypingUsers([]);
        setIsProcessing(false);
    }, [fetchAIResponse]);

    const onMessageEntered = useCallback(async ({ message, event }: ChatTypes.MessageEnteredEvent): Promise<void> => {
        insertMessage({ id: Date.now(), ...message });

        if (!alerts.length) {
            (event?.target as HTMLElement).blur();

            await processAIRequest(message);

            (event?.target as HTMLElement).focus();
        }
    }, [insertMessage, alerts.length, processAIRequest]);

    const messageRender = useCallback(({ message }: { message: ChatTypes.Message }) => <Message text={message.text} />, []);

    const onReset = () => {
        reset(aiGroupId ?? null);
    }

    return (
        <div className={'assistant-container'} ref={containerRef}>
            <div className={'assistant-toolbar'}>
                <Button icon="trash" onClick={onReset} />
            </div>
            <Chat
                className={[(isProcessing || isLoading) && CHAT_DISABLED_CLASS, isLoading && CHAT_LOADING_CLASS].filter(Boolean).join(' ')}
                dataSource={dataSource}
                reloadOnChange={false}
                showAvatar={false}
                showDayHeaders={false}
                user={user}
                height={'100%'}
                onMessageEntered={onMessageEntered}
                alerts={alerts}
                typingUsers={typingUsers}
                messageRender={messageRender}
            />
            <LoadPanel
                container={containerRef.current ?? undefined}
                position={{ of: containerRef.current ?? undefined }}
                shadingColor="transparent"
                visible={isLoading}
            />
        </div>
    );
}